import { isEqual } from 'lodash';
import { PublicationForm, PublicationGroupStatus, PublicationStatusEnum } from '@web/vin-sessions/types';
import { encodeDescriptionForWaf, isRichTextEmpty } from './description.utils';
import { parseVersion } from './version.utils';
import { isServiceDiagnosticsLabel, isTechTipsLabel } from './vehicle-filter.utils';

// Older documents still carry upload pipeline values (UPLOADING, UPLOADED, ...) in `status`
const LIFECYCLE_STATUSES: ReadonlyArray<string> = [
    PublicationStatusEnum.Draft,
    PublicationStatusEnum.InReview,
    PublicationStatusEnum.Published,
    PublicationStatusEnum.Archived,
];

export const isLifecycleStatus = (status: string | undefined): boolean =>
    Boolean(status && LIFECYCLE_STATUSES.includes(status));

export const prepareCreatePublicationBody = (publicationInfo: PublicationForm) => {
    return {
        pubVersion: parseVersion(publicationInfo.pubVersion),
        // The backend re-parents the document to this id on every update, so always send the
        // document's own parent. It is empty for a first version.
        parentDocumentId: publicationInfo.parentDocumentId,
        title: publicationInfo.title,
        // Description is rich text (HTML). Base64-encode it so AWS WAF's
        // CrossSiteScripting_BODY rule doesn't reject the request; the backend
        // Base64DecodingDeserializer decodes it back to HTML.
        description: encodeDescriptionForWaf(publicationInfo?.description),
        docCode: publicationInfo?.docCode,
        visibilityLevel:
            publicationInfo.visibilityLevel && publicationInfo.visibilityLevel.value !== ''
                ? publicationInfo.visibilityLevel.value
                : null,
        docTypeId: publicationInfo?.publicationType?.id,
        // Sending an upload pipeline status back would overwrite what the upload worker set
        status: isLifecycleStatus(publicationInfo?.status) ? publicationInfo.status : undefined,
        createdDate: publicationInfo.createdDate,
        fileName: publicationInfo.contentFile?.fileName || undefined,
        publishedYear: publicationInfo.publishedYear || undefined,
        publishedMonth: publicationInfo.publishedMonth || undefined,
        interactiveInd: isServiceDiagnosticsLabel(publicationInfo?.publicationType?.label)
            ? (publicationInfo.interactiveInd ?? true)
            : undefined,
    };
};

// What each save endpoint receives, compared against the last save to find what changed
export interface PublicationSnapshot {
    document: ReturnType<typeof prepareCreatePublicationBody>;
    tags: { dtcCodes: string[]; keyWords: string[]; toolNumbers: string[] };
}

export const toPublicationSnapshot = (publicationInfo: PublicationForm): PublicationSnapshot => ({
    document: prepareCreatePublicationBody(publicationInfo),
    tags: {
        dtcCodes: publicationInfo.dtcCodes ?? [],
        keyWords: publicationInfo.keywords ?? [],
        toolNumbers: publicationInfo.toolNumbers ?? [],
    },
});

// Typed fields wait for the debounce; picks and toggles save right away
const TYPED_FIELDS: ReadonlyArray<keyof PublicationSnapshot['document']> = [
    'title',
    'docCode',
    'description',
    'pubVersion',
];

export const shouldSaveImmediately = (previous: PublicationSnapshot, next: PublicationSnapshot): boolean => {
    if (!isEqual(previous.tags, next.tags)) {
        return true;
    }
    return (Object.keys(next.document) as Array<keyof PublicationSnapshot['document']>).some(
        (key) => !TYPED_FIELDS.includes(key) && !isEqual(previous.document[key], next.document[key])
    );
};

/** Shown when a save was rejected and the backend gave no message of its own. */
export const SAVE_FAILED_REASON = 'The last save failed';

export const BLOCKED_REASON = {
    missingTitleOrType: 'Add a title and publication type to start saving',
    zipNotAllowed: 'Zip files are only allowed for Service Diagnostics publications',
    fileNotReadyToPublish: 'Publish once the file is ready',
    missingRequiredForPublished: 'Fill in all required fields to save as Published',
    invalidVersion: 'Enter a version above 0 to save',
} as const;

export const getMissingRequiredFields = (publicationInfo: PublicationForm): string[] => {
    const isMissingPublishedDate =
        isTechTipsLabel(publicationInfo.publicationType?.label) &&
        (!publicationInfo.publishedYear || !publicationInfo.publishedMonth);
    const checks: Array<[string, boolean]> = [
        ['title', !publicationInfo.title || publicationInfo.title === 'Untitled'],
        ['publicationType', !publicationInfo.publicationType?.id],
        ['docCode', !publicationInfo.docCode],
        ['visibilityLevel', !publicationInfo.visibilityLevel?.value],
        ['contentFile', !publicationInfo.contentFile?.fileName],
        ['description', isRichTextEmpty(publicationInfo.description)],
        ['publishedDate', isMissingPublishedDate],
        ['pubVersion', !((parseVersion(publicationInfo.pubVersion) ?? 0) > 0)],
    ];
    return checks.filter(([, isMissing]) => isMissing).map(([field]) => field);
};

/**
 * A file still processing does not block a save, since the backend tracks it in `uploadStatus`.
 *
 * @param publicationInfo the form state
 * @param options whether the content file is ready to publish, and the status the backend last saved
 * @returns the reason to show the user, or null
 */
export const getAutosaveBlockedReason = (
    publicationInfo: PublicationForm,
    options: { canPublish?: boolean; savedStatus?: string } = {}
): string | null => {
    const hasTitle = Boolean(publicationInfo.title) && publicationInfo.title !== 'Untitled';
    if (!hasTitle || !publicationInfo.publicationType?.id) {
        return BLOCKED_REASON.missingTitleOrType;
    }

    const contentFile = publicationInfo.contentFile;
    const isZip = Boolean(
        contentFile?.fileName && (contentFile.fileName.toLowerCase().endsWith('.zip') || contentFile.fileType === 'ZIP')
    );
    if (isZip && publicationInfo.publicationType.label !== 'Service Diagnostics') {
        return BLOCKED_REASON.zipNotAllowed;
    }

    const isPublished = publicationInfo.status === PublicationGroupStatus.Published;
    // A published publication stays published while a replacement file processes
    const isBecomingPublished = isPublished && options.savedStatus !== PublicationGroupStatus.Published;
    if (isBecomingPublished && options.canPublish === false) {
        return BLOCKED_REASON.fileNotReadyToPublish;
    }
    if (isPublished && getMissingRequiredFields(publicationInfo).length > 0) {
        return BLOCKED_REASON.missingRequiredForPublished;
    }
    if (!((parseVersion(publicationInfo.pubVersion) ?? 0) > 0)) {
        return BLOCKED_REASON.invalidVersion;
    }

    return null;
};
